'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import type { UpsertMcpCredentialDto } from '@terra/shared-contracts-mcp-servers';
import type { McpDiscoveryAuthModelDto } from '@terra/shared-contracts-mcp-servers';
import {
  buildMcpCredential,
  toMcpFormCredentialType,
  type McpFormCredentialType,
} from '@terra/shared-contracts-mcp-servers';
import { Badge } from '../../components/badge';
import { Button } from '../../components/button';
import { Field } from '../../components/field';
import { Input } from '../../components/input';
import { Select, createSelectListCollection } from '../../components/select';
import { Text } from '../../components/text';
import { McpOAuthCredentialSection } from './McpOAuthCredentialSection';

type McpCredentialAuthorizeAction = {
  onClick: () => void;
  disabled?: boolean;
};

type McpCredentialFieldsProps = {
  discoveryUrl: string;
  isEditing: boolean;
  initialCredentialType?: string | null;
  initialAvailableOAuthScopes?: string[];
  initialOauthScopes?: string;
  onCredentialChange: (credential: UpsertMcpCredentialDto | null) => void;
  onCredentialTypeChange?: (credentialType: McpFormCredentialType) => void;
  disabled?: boolean;
  showNoneConfirmButton?: boolean;
  hideAuthTypeSelector?: boolean;
  authorizeAction?: McpCredentialAuthorizeAction;
  discoverOAuthScopes?: (
    discoveryUrl: string,
  ) => Promise<McpDiscoveryAuthModelDto | null>;
};

export function McpCredentialFields({
  discoveryUrl,
  isEditing,
  initialCredentialType,
  initialAvailableOAuthScopes,
  initialOauthScopes,
  onCredentialChange,
  onCredentialTypeChange,
  disabled,
  showNoneConfirmButton,
  hideAuthTypeSelector = false,
  authorizeAction,
  discoverOAuthScopes,
}: McpCredentialFieldsProps) {
  const t = useTranslations('designSystem.settings.mcpCredentialFields');
  const [credentialType, setCredentialType] = useState<McpFormCredentialType>(
    toMcpFormCredentialType(initialCredentialType),
  );
  const [bearerToken, setBearerToken] = useState('');
  const [apiKeyHeader, setApiKeyHeader] = useState('X-API-Key');
  const [apiKey, setApiKey] = useState('');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [oauthAuthorizeUrl, setOauthAuthorizeUrl] = useState('');
  const [oauthTokenUrl, setOauthTokenUrl] = useState('');
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [oauthScopes, setOauthScopes] = useState('');
  const [availableOAuthScopes, setAvailableOAuthScopes] = useState<string[]>(
    [],
  );
  const [isDiscoveringOAuthScopes, setIsDiscoveringOAuthScopes] =
    useState(false);
  const oauthScopesCustomizedRef = useRef(false);
  const oauthScopeDiscoveryRequestIdRef = useRef(0);

  useEffect(() => {
    const seededRequestedScopes = initialOauthScopes?.trim() ?? '';

    oauthScopesCustomizedRef.current = seededRequestedScopes.length > 0;
    if (initialAvailableOAuthScopes && initialAvailableOAuthScopes.length > 0) {
      setAvailableOAuthScopes(initialAvailableOAuthScopes);
      setOauthScopes(
        seededRequestedScopes || initialAvailableOAuthScopes.join(' '),
      );

      return;
    }

    setAvailableOAuthScopes([]);
    setOauthScopes(seededRequestedScopes);
  }, [initialAvailableOAuthScopes, initialOauthScopes]);

  useEffect(() => {
    if (
      !discoverOAuthScopes ||
      (credentialType !== 'OAUTH' && credentialType !== 'OAUTH_DCR')
    ) {
      setAvailableOAuthScopes([]);
      setIsDiscoveringOAuthScopes(false);

      return;
    }

    const trimmedDiscoveryUrl = discoveryUrl.trim();

    if (!trimmedDiscoveryUrl) {
      setAvailableOAuthScopes([]);
      setIsDiscoveringOAuthScopes(false);

      return;
    }

    const requestId = oauthScopeDiscoveryRequestIdRef.current + 1;

    oauthScopeDiscoveryRequestIdRef.current = requestId;
    const timeoutId = window.setTimeout(() => {
      setIsDiscoveringOAuthScopes(true);
      void discoverOAuthScopes(trimmedDiscoveryUrl)
        .then((result) => {
          if (oauthScopeDiscoveryRequestIdRef.current !== requestId) {
            return;
          }

          const scopes = result?.scopesSupported ?? [];

          setAvailableOAuthScopes((currentScopes) =>
            scopes.length > 0 ? scopes : currentScopes,
          );
          if (scopes.length > 0 && !oauthScopesCustomizedRef.current) {
            setOauthScopes(scopes.join(' '));
          }
        })
        .catch(() => {
          if (oauthScopeDiscoveryRequestIdRef.current !== requestId) {
            return;
          }
        })
        .finally(() => {
          if (oauthScopeDiscoveryRequestIdRef.current !== requestId) {
            return;
          }

          setIsDiscoveringOAuthScopes(false);
        });
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [credentialType, discoverOAuthScopes, discoveryUrl]);

  const handleOAuthScopesChange = useCallback((value: string) => {
    oauthScopesCustomizedRef.current = true;
    setOauthScopes(value);
  }, []);

  const handleOAuthScopeSelectionChange = useCallback(() => {
    oauthScopesCustomizedRef.current = true;
  }, []);

  useEffect(() => {
    if (!initialCredentialType) {
      return;
    }

    setCredentialType(toMcpFormCredentialType(initialCredentialType));
  }, [initialCredentialType]);

  const authTypeOptions = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { label: t('authTypeNone'), value: 'NONE' },
          { label: t('authTypeBearer'), value: 'BEARER' },
          { label: t('authTypeApiKey'), value: 'API_KEY' },
          { label: t('authTypeBasic'), value: 'BASIC' },
          {
            label: t('authTypeOAuth'),
            value: 'OAUTH',
            badge: t('badgeManual'),
          },
          {
            label: t('authTypeOAuth'),
            value: 'OAUTH_DCR',
            badge: t('badgeAutoDiscover'),
          },
        ],
      }),
    [t],
  );

  const selectedAuthType = authTypeOptions.items.find(
    (item) => item.value === credentialType,
  );

  const buildCredentialInput = useCallback(
    (type: McpFormCredentialType) =>
      buildMcpCredential({
        credentialType: type,
        bearerToken,
        apiKeyHeader,
        apiKey,
        basicUsername,
        basicPassword,
        oauthAuthorizeUrl,
        oauthTokenUrl,
        oauthClientId,
        oauthClientSecret,
        oauthScopes,
        discoveryUrl,
        isEditing,
      }),
    [
      apiKey,
      apiKeyHeader,
      basicPassword,
      basicUsername,
      bearerToken,
      discoveryUrl,
      isEditing,
      oauthAuthorizeUrl,
      oauthClientId,
      oauthClientSecret,
      oauthScopes,
      oauthTokenUrl,
    ],
  );

  const emitCredentialForType = useCallback(
    (type: McpFormCredentialType) => {
      onCredentialChange(buildCredentialInput(type));
    },
    [buildCredentialInput, onCredentialChange],
  );

  const handleCredentialTypeChange = (nextType: McpFormCredentialType) => {
    setCredentialType(nextType);
    onCredentialTypeChange?.(nextType);
    emitCredentialForType(nextType);
  };

  useEffect(() => {
    onCredentialTypeChange?.(credentialType);
  }, [credentialType, onCredentialTypeChange]);

  const updateField = useCallback(
    <T,>(setter: (value: T) => void, value: T) => {
      setter(value);
    },
    [],
  );

  useEffect(() => {
    emitCredentialForType(credentialType);
  }, [
    bearerToken,
    apiKeyHeader,
    apiKey,
    basicUsername,
    basicPassword,
    oauthAuthorizeUrl,
    oauthTokenUrl,
    oauthClientId,
    oauthClientSecret,
    oauthScopes,
    credentialType,
    emitCredentialForType,
  ]);

  return (
    <VStack alignItems="stretch" gap={4}>
      {!hideAuthTypeSelector ? (
        <Field.Root w="full">
          <Field.Label>{t('fieldAuth')}</Field.Label>
          <Select.Root
            collection={authTypeOptions}
            value={[credentialType]}
            onValueChange={(details) =>
              handleCredentialTypeChange(
                toMcpFormCredentialType(details.value[0] ?? null),
              )
            }
            disabled={disabled}
            positioning={{ sameWidth: true, strategy: 'fixed' }}
          >
            <Select.Control>
              <Select.Trigger>
                <HStack gap="2">
                  <Text>{selectedAuthType?.label}</Text>
                  {selectedAuthType?.badge ? (
                    <Badge variant="fill">{selectedAuthType.badge}</Badge>
                  ) : null}
                </HStack>
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {authTypeOptions.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Select.ItemText>
                      <HStack gap="2">
                        <Text>{item.label}</Text>
                        {item.badge ? (
                          <Badge variant="fill">{item.badge}</Badge>
                        ) : null}
                      </HStack>
                    </Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Field.Root>
      ) : null}

      {credentialType === 'BEARER' ? (
        <Field.Root w="full">
          <Field.Label>{t('fieldBearerToken')}</Field.Label>
          <Input
            type="password"
            value={bearerToken}
            placeholder={isEditing ? t('placeholderKeepExisting') : undefined}
            onChange={(event) =>
              updateField(setBearerToken, event.target.value)
            }
            disabled={disabled}
          />
        </Field.Root>
      ) : null}

      {credentialType === 'API_KEY' ? (
        <VStack gap={4} w="full">
          <Field.Root w="full">
            <Field.Label>{t('fieldHeaderName')}</Field.Label>
            <Input
              value={apiKeyHeader}
              onChange={(event) =>
                updateField(setApiKeyHeader, event.target.value)
              }
              disabled={disabled}
            />
          </Field.Root>
          <Field.Root w="full">
            <Field.Label>{t('fieldApiKey')}</Field.Label>
            <Input
              type="password"
              value={apiKey}
              placeholder={isEditing ? t('placeholderKeepExisting') : undefined}
              onChange={(event) => updateField(setApiKey, event.target.value)}
              disabled={disabled}
            />
          </Field.Root>
        </VStack>
      ) : null}

      {credentialType === 'BASIC' ? (
        <VStack gap={4} w="full">
          <Field.Root w="full">
            <Field.Label>{t('fieldUsername')}</Field.Label>
            <Input
              value={basicUsername}
              onChange={(event) =>
                updateField(setBasicUsername, event.target.value)
              }
              disabled={disabled}
            />
          </Field.Root>
          <Field.Root w="full">
            <Field.Label>{t('fieldPassword')}</Field.Label>
            <Input
              type="password"
              value={basicPassword}
              placeholder={isEditing ? t('placeholderKeepExisting') : undefined}
              onChange={(event) =>
                updateField(setBasicPassword, event.target.value)
              }
              disabled={disabled}
            />
          </Field.Root>
        </VStack>
      ) : null}

      {credentialType === 'OAUTH' || credentialType === 'OAUTH_DCR' ? (
        <McpOAuthCredentialSection
          credentialType={credentialType}
          isEditing={isEditing}
          disabled={disabled}
          oauthAuthorizeUrl={oauthAuthorizeUrl}
          oauthTokenUrl={oauthTokenUrl}
          oauthClientId={oauthClientId}
          oauthClientSecret={oauthClientSecret}
          oauthScopes={oauthScopes}
          availableOAuthScopes={availableOAuthScopes}
          isDiscoveringOAuthScopes={isDiscoveringOAuthScopes}
          authorizeAction={authorizeAction}
          onOauthAuthorizeUrlChange={(value) =>
            updateField(setOauthAuthorizeUrl, value)
          }
          onOauthTokenUrlChange={(value) =>
            updateField(setOauthTokenUrl, value)
          }
          onOauthClientIdChange={(value) =>
            updateField(setOauthClientId, value)
          }
          onOauthClientSecretChange={(value) =>
            updateField(setOauthClientSecret, value)
          }
          onOauthScopesChange={handleOAuthScopesChange}
          onOAuthScopeSelectionChange={handleOAuthScopeSelectionChange}
        />
      ) : null}

      {showNoneConfirmButton && credentialType === 'NONE' ? (
        <Button
          variant="outline"
          size="sm"
          alignSelf="flex-start"
          disabled={disabled}
          onClick={() => onCredentialChange({ type: 'NONE' })}
        >
          {t('authTypeNone')}
        </Button>
      ) : null}
    </VStack>
  );
}
